import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchMessagesData } from '../services/dataApi';
import PageLoader from '../components/PageLoader';
import MessagesTable from '../components/MessagesTable';
import SearchBar from '../components/SearchBar';
import { searchInData } from '../utils/searchUtils';
import useUrlPagination from '../hooks/useUrlPagination';
import useDebouncedValue from '../hooks/useDebouncedValue';
import './Page.css';

const Messages = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentPage, setCurrentPage, itemsPerPage } = useUrlPagination();
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);

  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchMessagesData(page, itemsPerPage, debouncedSearchTerm);
      setData(result.data || result);
      setPagination(result.pagination);
    } catch (err) {
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage, loadData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter data on client side
  const filteredData = useMemo(() => {
    if (!data) return null;
    
    // Extract data array
    let messages = [];
    if (data && data.data && Array.isArray(data.data)) {
      messages = data.data;
    } else if (Array.isArray(data)) {
      messages = data;
    } else if (data && data.ops && data.ops.length > 0) {
      const firstOp = data.ops[0];
      if (firstOp.list && Array.isArray(firstOp.list)) {
        messages = firstOp.list;
      } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
        messages = firstOp.data.list;
      }
    }

    if (!searchTerm || searchTerm.trim() === '') {
      return data;
    }

    const filtered = searchInData(messages, searchTerm);
    
    // Preserve data structure
    if (data && data.data && Array.isArray(data.data)) {
      return { ...data, data: filtered };
    } else if (Array.isArray(data)) {
      return filtered;
    } else {
      return { ...data, ops: [{ ...data.ops[0], list: filtered }] };
    }
  }, [data, searchTerm]);

  const messagesCount = pagination?.total ?? (filteredData?.data?.length || 0);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <h1>Messages</h1>
          <p>Review incoming operational messages in a calmer, more readable feed.</p>
        </div>
        <div className="page-overview">
          <div className="overview-card">
            <div className="overview-label">Feed</div>
            <div className="overview-value">{messagesCount}</div>
            <div className="overview-meta">Visible messages</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Search</div>
            <div className="overview-value">{searchTerm ? 'On' : 'Off'}</div>
            <div className="overview-meta">{searchTerm ? `Filter: ${searchTerm}` : 'Scanning full feed'}</div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <PageLoader loading={loading} error={error}>
          {data ? (
            <div className="page-data">
              <div className="page-section-header">
                <div className="page-section-title">
                  <h2>Message Feed</h2>
                  <p>Open a message to inspect the raw payload.</p>
                </div>
                <div className="page-section-meta">
                  <div className="page-chip">50 messages per page</div>
                  <div className="page-chip">{searchTerm ? 'Filtered feed' : 'Recent first'}</div>
                </div>
              </div>
              <SearchBar 
                onSearch={handleSearch}
                value={searchTerm}
                resultCount={pagination?.total || 0}
                isUpdating={searchTerm !== debouncedSearchTerm}
                placeholder="Search messages..."
              />
              <MessagesTable 
                data={filteredData} 
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <div className="page-placeholder">
              <p>No data received</p>
            </div>
          )}
        </PageLoader>
      </div>
    </div>
  );
};

export default Messages;
